import { Navigate, createBrowserRouter } from 'react-router-dom';

import InsuredProtectedRoute from '@/InsuredPortal/registration/InsuredProtectedRoute';
import Login from '@modules/access/views/Login/Login.jsx';

import { lazyWithRetry } from './lazyWithRetry';
import { ProtectedRoute } from './RouteGuards';

// /asegurado/login elige entre el login actual (solo cédula) y el flujo de
// registro / código OTP según INSURED_REGISTRATION_ENABLED
// (src/InsuredPortal/registration/config.js).
const InsuredLoginSwitch = lazyWithRetry(
  () => import('@/InsuredPortal/registration/InsuredLoginSwitch')
);
const InsuredTypeSelect = lazyWithRetry(() => import('@/InsuredPortal/InsuredTypeSelect'));
const InsuredDashboard = lazyWithRetry(() => import('@/InsuredPortal/InsuredDashboard'));
const InsuredNewRequest = lazyWithRetry(() => import('@/InsuredPortal/InsuredNewRequest'));
const InsuredRequestDetail = lazyWithRetry(() => import('@/InsuredPortal/InsuredRequestDetail'));
const InsuredRequestReimbursements = lazyWithRetry(
  () => import('@/InsuredPortal/InsuredRequestReimbursements')
);

const InsuredProfile = lazyWithRetry(() => import('@/InsuredPortal/InsuredProfile'));

const Colectivos = lazyWithRetry(() => import('@/modules/access/views/Admin/Colectivos.jsx'));
const Actualizaciones = lazyWithRetry(
  () => import('@/modules/access/views/Admin/Actualizaciones.jsx')
);
const SyncCsv = lazyWithRetry(() => import('@/modules/access/views/Admin/SyncCsv.jsx'));

const ServiceOrdersList = lazyWithRetry(
  () => import('@/MedicalCases/ServiceOrders/ServiceOrdersList.jsx')
);
const CreateServiceOrder = lazyWithRetry(
  () => import('@/MedicalCases/ServiceOrders/CreateServiceOrder.jsx')
);

const CaseDetail = lazyWithRetry(() => import('@/MedicalCases/CaseDetail'));
const CreateCaseV3 = lazyWithRetry(() => import('@/MedicalCases/CreateCaseV3'));

const CaseMasive = lazyWithRetry(() => import('@/MedicalCases/CaseMasive'));
const MedicalCasesListingV3 = lazyWithRetry(() => import('@/MedicalCases/MedicalCasesListingV3'));

const EditCaseV3 = lazyWithRetry(() => import('@/MedicalCases/EditCaseV3'));
const MedicalCasesSubscribeList = lazyWithRetry(
  () => import('@/MedicalCases/Subscribe/MedicalCasesSubscribeList')
);
const MedicalStaffSelector = lazyWithRetry(
  () => import('@modules/clinical/MedicalStaff/views/MedicalStaffSelector')
);
const InvoiceList = lazyWithRetry(() => import('@/modules/billing/Invoices/views/InvoiceList'));
const CreateInvoice = lazyWithRetry(() => import('@/modules/billing/Invoices/views/CreateInvoice'));
const Chat = lazyWithRetry(() => import('@/modules/chat/views/Chat'));

const AssignCaseRequest = lazyWithRetry(
  () => import('@/MedicalCases/CaseRequest/AssignCaseRequest')
);
const PendingRequestList = lazyWithRetry(
  () => import('@/MedicalCases/CaseRequest/PendingRequestList')
);
const ReassignCase = lazyWithRetry(() => import('@/MedicalCases/SubAdministrator/ReassignCase'));
const StaleCasesListing = lazyWithRetry(() => import('@/MedicalCases/StaleCasesListing'));
const AssignedCasesListing = lazyWithRetry(
  () => import('@/MedicalCases/SubAdministrator/AssignedCases')
);

const ImagingStudyList = lazyWithRetry(() => import('@modules/imaging/features/imagingStudyList'));

const DetailImagingStudyV2 = lazyWithRetry(
  () => import('@modules/imaging/DetailImagingStudyV2/views/DetailImagingStudyV2')
);
const ImagingStudyAudio = lazyWithRetry(() => import('@modules/imaging/views/ImagingStudyAudio'));

const ImagingMetrics = lazyWithRetry(() => import('@modules/imaging/views/ImagingMetrics'));

const Audio = lazyWithRetry(() => import('@/MedicalCases/audio'));
const GenerateReport = lazyWithRetry(
  () => import('@modules/imaging/features/generateReport/index')
);

const DoctorR = lazyWithRetry(() => import('@modules/clinical/MedicalStaff/views/doctor'));
const DoctorEditPage = lazyWithRetry(() => import('@modules/clinical/MedicalStaff/views/doctorE'));

const DoctorProfilePage = lazyWithRetry(
  () => import('@modules/clinical/MedicalStaff/views/DoctorProfile')
);
const DoctorStatistics = lazyWithRetry(
  () => import('@/modules/analytics/doctors-analytics/views/DoctorStatistics')
);
const ImagenMedAnalytics = lazyWithRetry(
  () => import('@/modules/analytics/imagenmed-analytics/views/ImagenMedAnalytics')
);

const Report = lazyWithRetry(() => import('@/MedicalCases/report'));

const StudyViewer = lazyWithRetry(() => import('@modules/imaging/viewer/StudyViewer/StudyViewer'));
const CornerstoneStudyURLLazy = lazyWithRetry(
  () => import('@modules/imaging/viewer/study/CornerstoneStudyURL')
);
const CreateImagingStudy = lazyWithRetry(
  () => import('@modules/imaging/CreateImagingStudyV3/views/CreateImagingStudyV3')
);

const RealtimeTestLazy = lazyWithRetry(() => import('@/modules/realtime/views/RealtimeTest'));

const DNISearch = lazyWithRetry(() => import('@modules/imaging/ImagenMed/DNISearch'));
const DoctorStudies = lazyWithRetry(() => import('@modules/imaging/ImagenMed/DoctorStudies'));
// const DoctorStudiesDetail = lazyWithRetry(() => import('@/ImagenMed/DoctorStudiesDetail'));
const DoctorStudiesDetailV2 = lazyWithRetry(
  () => import('@modules/imaging/ImagenMed/detail-v2/DoctorStudiesDetailV2')
);

const Logout = lazyWithRetry(() => import('@/modules/access/views/Logout/Logout'));
const ClientScaleView = lazyWithRetry(
  () => import('@modules/billing/Baremos/views/ClientScaleView')
);
const CreateBaremo = lazyWithRetry(() => import('@modules/billing/Baremos/views/CreateBaremo'));
const EditBaremo = lazyWithRetry(() => import('@modules/billing/Baremos/views/EditBaremo'));
const ScaleSelectClient = lazyWithRetry(
  () => import('@modules/billing/Baremos/views/ScaleSelectClient')
);
const ImagenMedScaleView = lazyWithRetry(
  () => import('@modules/billing/Baremos/views/ImagenMedScaleView')
);
const HomologacionesListing = lazyWithRetry(
  () => import('@modules/billing/Baremos/views/HomologacionesListing')
);

// Carta Aval (Letter of Guarantee)
const LetterOfGuaranteeList = lazyWithRetry(() => import('@/LetterOfGuarantee/List'));
const LetterOfGuaranteeCreate = lazyWithRetry(() => import('@/LetterOfGuarantee/Create'));
const LetterOfGuaranteeEdit = lazyWithRetry(() => import('@/LetterOfGuarantee/Edit'));
const LetterOfGuaranteeDetail = lazyWithRetry(() => import('@/LetterOfGuarantee/Detail'));

// Reembolso (Reimbursement) — staff
const ReimbursementList = lazyWithRetry(() => import('@/Reimbursement/List'));
const ReimbursementCreate = lazyWithRetry(() => import('@/Reimbursement/Create'));
const ReimbursementDetail = lazyWithRetry(() => import('@/Reimbursement/Detail'));
const ReimbursementEdit = lazyWithRetry(() => import('@/Reimbursement/Edit'));

const Balance = lazyWithRetry(() => import('@/modules/billing/views/Balance'));

const TermsConditions = lazyWithRetry(() => import('@/core/TermsConditions/TermsConditions'));
const PrivacyPolicies = lazyWithRetry(() => import('@/core/TermsConditions/PrivacyPolicies'));

const AnnulledCasesList = lazyWithRetry(() => import('@/MedicalCases/AnnulledCasesList'));
const FundListing = lazyWithRetry(() => import('@/modules/billing/Funds/views/FundListing'));
const CreateFund = lazyWithRetry(() => import('@/modules/billing/Funds/views/CreateFund'));
const PaymentDetailsListing = lazyWithRetry(
  () => import('@/modules/billing/Funds/views/PaymentDetailsListing')
);
const ClientPaymentView = lazyWithRetry(
  () => import('@modules/billing/Payment/views/ClientPaymentView')
);
const PaymentDetailView = lazyWithRetry(
  () => import('@modules/billing/Payment/views/PaymentDetailView')
);
const PaymentSelectClient = lazyWithRetry(
  () => import('@modules/billing/Payment/views/PaymentSelectClient')
);
const PaymentSummary = lazyWithRetry(
  () => import('@modules/billing/Payment/components/PaymentSummary')
);
const PaymentCheckout = lazyWithRetry(
  () => import('@modules/billing/Payment/views/PaymentCheckout')
);
const PaymentHistory = lazyWithRetry(() => import('@modules/billing/Payment/views/PaymentHistory'));

const ClientPaymentList = lazyWithRetry(
  () => import('@modules/billing/Payment/views/ClientPaymentList')
);

const MdAbroadBillingView = lazyWithRetry(
  () => import('@modules/billing/Payment/views/MdAbroadBillingView')
);

const MdAbroadPaymentDetail = lazyWithRetry(
  () => import('@modules/billing/Payment/views/MdAbroadPaymentDetail')
);

const GenerateToken = lazyWithRetry(() => import('@modules/access/views/Token/generateToken'));
const CreateAdvance = lazyWithRetry(() => import('@/modules/billing/Advances/views/CreateAdvance'));
const MedicalCenterAdvances = lazyWithRetry(
  () => import('@/modules/billing/Advances/views/MedicalCenterAdvances')
);
const AdvanceListing = lazyWithRetry(
  () => import('@/modules/billing/Advances/views/AdvanceListing')
);

const UserPermissionsPage = lazyWithRetry(
  () => import('@/modules/access/views/ManageUsers/UserPermissions')
);
const Permissions = lazyWithRetry(() => import('@/modules/access/views/ManageUsers/Permissions'));

const Users = lazyWithRetry(() => import('@/modules/access/views/ManageUsers/Users'));
const UsersClient = lazyWithRetry(() => import('@/modules/access/views/ManageUsers/UsersClient'));

const Maintenance = lazyWithRetry(() => import('@/modules/access/views/Maintenance/Maintenance'));
const BatchInvoice = lazyWithRetry(() => import('@/modules/billing/Invoices/views/BatchInvoice'));
const ConciliateInvoicesPage = lazyWithRetry(
  () => import('@/modules/billing/Invoices/views/ConciliateInvoicesPage')
);

const Person = lazyWithRetry(() => import('@/Person/Person'));
const PersonListing = lazyWithRetry(() => import('@/Person/PersonListing'));

const MedicalCentersListing = lazyWithRetry(
  () => import('@modules/clinical/MedicalCenters/views/MedicalCentersListing')
);
const CreateMedicalCenter = lazyWithRetry(
  () => import('@modules/clinical/MedicalCenters/views/CreateMedicalCenter')
);
const EditMedicalCenter = lazyWithRetry(
  () => import('@modules/clinical/MedicalCenters/views/EditMedicalCenter')
);
const RequirementsListing = lazyWithRetry(
  () => import('@/modules/clinical/views/RequirementsListing')
);
const MedicalCaseReportList = lazyWithRetry(
  () => import('@/modules/clinical/views/MedicalCaseReportList')
);
const Pattern = lazyWithRetry(() => import('@/MedicalCases/form_patterns'));

const AnalyticsPage = lazyWithRetry(
  () => import('@/modules/analytics/medical-cases-analytics/views/Analytics')
);

const MedicalCenterProfile = lazyWithRetry(
  () => import('@modules/imaging/equipment/EquipmentManager')
);
const SubAdminScaleView = lazyWithRetry(
  () => import('@modules/billing/Baremos/views/SubAdminScaleView')
);

const Dria = lazyWithRetry(() => import('@/Dria/dria_view'));

const PatientsListing = lazyWithRetry(
  () => import('@modules/clinical/patients/views/PatientsListing')
);

const router = createBrowserRouter([
  {
    path: '/',
    element: <Login />,
    errorElement: <Navigate to="/login" />,
  },
  { path: '/login', element: <Login /> },
  { path: '/logout', element: <Logout /> },
  {
    path: '/usuarios',
    element: (
      <ProtectedRoute path="/usuarios">
        <Users />
      </ProtectedRoute>
    ),
  },
  {
    path: '/usuarios/cliente',
    element: (
      <ProtectedRoute path="/usuarios">
        <UsersClient />
      </ProtectedRoute>
    ),
  },
  {
    path: '/usuarios/permisos/:id',
    element: (
      <ProtectedRoute path="/usuarios/permisos/:id">
        <UserPermissionsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/permisos',
    element: (
      <ProtectedRoute path="/permisos">
        <Permissions />
      </ProtectedRoute>
    ),
  },
  {
    path: '/persona/editar/:personId',
    element: (
      <ProtectedRoute path="/casos/nuevo">
        <Person />
      </ProtectedRoute>
    ),
  },
  {
    path: '/asegurados',
    element: (
      <ProtectedRoute path="/asegurados">
        <PersonListing />
      </ProtectedRoute>
    ),
  },
  /* FEATURE: /casos — el wrapper router decide V1/V3 según toggle global. */
  {
    path: '/casos',
    element: (
      <ProtectedRoute path="/casos">
        <MedicalCasesListingV3 />
      </ProtectedRoute>
    ),
  },
  {
    path: '/casos/anulados',
    element: (
      <ProtectedRoute path="/casos/anulados">
        <AnnulledCasesList />
      </ProtectedRoute>
    ),
  },
  /* FEATURE: /casos/nuevo — wrapper router (V1/V3) */
  {
    path: '/casos/nuevo',
    element: (
      <ProtectedRoute path="/casos/nuevo">
        <CreateCaseV3 />
      </ProtectedRoute>
    ),
  },
  {
    path: '/ordenes-servicio/nueva',
    element: (
      <ProtectedRoute path="/ordenes-servicio/nueva">
        <CreateServiceOrder />
      </ProtectedRoute>
    ),
  },
  {
    path: '/ordenes-servicio',
    element: (
      <ProtectedRoute path="/ordenes-servicio">
        <ServiceOrdersList />
      </ProtectedRoute>
    ),
  },
  /* FEATURE: /casos/nuevo/:caseId — flujo de duplicar caso. Wrapper router. */
  {
    path: '/casos/nuevo/:caseId',
    element: (
      <ProtectedRoute path="/casos/nuevo">
        <CreateCaseV3 />
      </ProtectedRoute>
    ),
  },
  /* FEATURE: /casos/:caseId/editar — wrapper router (V1/V3). */
  {
    path: '/casos/:caseId/editar',
    element: (
      <ProtectedRoute path="/casos/:caseId/editar">
        <EditCaseV3 />
      </ProtectedRoute>
    ),
  },
  {
    path: '/casos/carga-masiva',
    element: (
      <ProtectedRoute path="/casos/carga-masiva">
        <CaseMasive />
      </ProtectedRoute>
    ),
  },
  {
    path: '/casos/audio',
    element: (
      <ProtectedRoute path="/casos/audio">
        <Audio />
      </ProtectedRoute>
    ),
  },
  {
    path: '/casos/patrones',
    element: (
      <ProtectedRoute path="/casos/patrones">
        <Pattern />
      </ProtectedRoute>
    ),
  },
  {
    path: '/casos/facturacion-lotes',
    element: (
      <ProtectedRoute path="/casos/facturacion-lotes">
        <BatchInvoice />
      </ProtectedRoute>
    ),
  },
  {
    path: '/conciliar',
    element: (
      <ProtectedRoute path="/conciliar">
        <ConciliateInvoicesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/casos/:caseId',
    element: (
      <ProtectedRoute path="/casos/:caseId">
        <CaseDetail />
      </ProtectedRoute>
    ),
  },
  {
    path: '/casos/:caseId/reasignar',
    element: (
      <ProtectedRoute path="/casos/:caseId/reasignar">
        <ReassignCase />
      </ProtectedRoute>
    ),
  },
  {
    path: '/casos/reasignados/:caseId',
    element: (
      <ProtectedRoute path="/casos/reasignados/:caseId">
        <CaseDetail />
      </ProtectedRoute>
    ),
  },
  {
    path: '/casos/por-suscribir',
    element: (
      <ProtectedRoute path="/casos/por-suscribir">
        <MedicalCasesSubscribeList />
      </ProtectedRoute>
    ),
  },
  {
    path: '/casos/por-suscribir/:caseId',
    element: (
      <ProtectedRoute path="/casos/por-suscribir/:caseId">
        <CaseDetail backButtonUrl="/casos/por-suscribir" />
      </ProtectedRoute>
    ),
  },
  {
    path: '/casos/reasignados',
    element: (
      <ProtectedRoute path="/casos/reasignados">
        <AssignedCasesListing />
      </ProtectedRoute>
    ),
  },
  {
    path: '/aval',
    element: (
      <ProtectedRoute path="/aval">
        <LetterOfGuaranteeList />
      </ProtectedRoute>
    ),
  },
  {
    path: '/aval/nuevo',
    element: (
      <ProtectedRoute path="/aval/nuevo">
        <LetterOfGuaranteeCreate />
      </ProtectedRoute>
    ),
  },
  {
    path: '/aval/:id',
    element: (
      <ProtectedRoute path="/aval/:id">
        <LetterOfGuaranteeDetail />
      </ProtectedRoute>
    ),
  },
  {
    path: '/aval/:id/editar',
    element: (
      <ProtectedRoute path="/aval/:id/editar">
        <LetterOfGuaranteeEdit />
      </ProtectedRoute>
    ),
  },
  {
    path: '/reembolso',
    element: (
      <ProtectedRoute path="/reembolso">
        <ReimbursementList />
      </ProtectedRoute>
    ),
  },
  {
    path: '/reembolso/nuevo',
    element: (
      <ProtectedRoute path="/reembolso/nuevo">
        <ReimbursementCreate />
      </ProtectedRoute>
    ),
  },
  {
    path: '/reembolso/:id',
    element: (
      <ProtectedRoute path="/reembolso/:id">
        <ReimbursementDetail />
      </ProtectedRoute>
    ),
  },
  {
    path: '/reembolso/:id/editar',
    element: (
      <ProtectedRoute path="/reembolso/:id/editar">
        <ReimbursementEdit />
      </ProtectedRoute>
    ),
  },
  {
    path: '/casos/solicitudes-pendientes',
    element: (
      <ProtectedRoute path="/casos/solicitudes-pendientes">
        <PendingRequestList />
      </ProtectedRoute>
    ),
  },
  {
    path: '/casos/:caseId/asignar-solicitud',
    element: (
      <ProtectedRoute path="/casos/:caseId/asignar-solicitud">
        <AssignCaseRequest />
      </ProtectedRoute>
    ),
  },
  {
    path: '/medicos',
    element: (
      <ProtectedRoute path="/medicos">
        <MedicalStaffSelector />
      </ProtectedRoute>
    ),
  },
  {
    path: '/casos-pendientes',
    element: (
      <ProtectedRoute path="/casos-pendientes">
        <StaleCasesListing />
      </ProtectedRoute>
    ),
  },
  {
    path: '/anticipos',
    element: (
      <ProtectedRoute path="/anticipos">
        <AdvanceListing />
      </ProtectedRoute>
    ),
  },
  {
    path: '/anticipos/nuevo',
    element: (
      <ProtectedRoute path="/anticipos/nuevo">
        <CreateAdvance />
      </ProtectedRoute>
    ),
  },
  {
    path: '/anticipos/proveedores/:centerId',
    element: (
      <ProtectedRoute path="/anticipos/proveedores/:centerId">
        <MedicalCenterAdvances />
      </ProtectedRoute>
    ),
  },
  {
    path: '/baremos',
    element: (
      <ProtectedRoute path="/baremos">
        <ScaleSelectClient />
      </ProtectedRoute>
    ),
  },
  {
    path: '/baremos/sub-administradora/:subAdminId',
    element: (
      <ProtectedRoute path="/baremos/sub-administradora/:subAdminId">
        <SubAdminScaleView />
      </ProtectedRoute>
    ),
  },
  {
    path: '/baremos/imagenmed',
    element: (
      <ProtectedRoute path="/baremos/imagenmed">
        <ImagenMedScaleView />
      </ProtectedRoute>
    ),
  },
  {
    path: '/baremos/homologaciones',
    element: (
      <ProtectedRoute path="/baremos/homologaciones">
        <HomologacionesListing />
      </ProtectedRoute>
    ),
  },
  {
    path: '/baremos/cliente/:clientId',
    element: (
      <ProtectedRoute path="/baremos/cliente/:clientId">
        <ClientScaleView />
      </ProtectedRoute>
    ),
  },
  {
    path: '/baremos/nuevo',
    element: (
      <ProtectedRoute path="/baremos/nuevo">
        <CreateBaremo />
      </ProtectedRoute>
    ),
  },
  {
    path: '/baremos/:baremoId',
    element: (
      <ProtectedRoute path="/baremos/:baremoId">
        <EditBaremo />
      </ProtectedRoute>
    ),
  },
  {
    path: '/saldo',
    element: (
      <ProtectedRoute path="/saldo">
        <Balance />
      </ProtectedRoute>
    ),
  },
  {
    path: '/pagos/clientes',
    element: <PaymentSelectClient />,
  },
  {
    path: '/pagos/resumen',
    element: <PaymentSummary />,
  },
  {
    path: '/pagos/checkout',
    element: <PaymentCheckout />,
  },
  {
    path: '/pagos/historial',
    element: <PaymentHistory />,
  },
  {
    path: '/pagos/clientes/:clientId',
    element: <ClientPaymentView />,
  },

  {
    path: '/pagos/mdabroad-cobrar',
    element: (
      <ProtectedRoute path="/pagos/mdabroad-cobrar">
        <MdAbroadBillingView />
      </ProtectedRoute>
    ),
  },
  {
    path: '/pagos/mdabroad-caso/:caseId',
    element: (
      <ProtectedRoute path="/pagos/mdabroad-caso/:caseId">
        <MdAbroadPaymentDetail />
      </ProtectedRoute>
    ),
  },

  {
    path: '/pagos/cuentas-por-pagar',
    element: (
      <ProtectedRoute path="/pagos/cuentas-por-pagar">
        <ClientPaymentList />
      </ProtectedRoute>
    ),
  },

  {
    path: '/pagos/detalle/:billingID',
    element: <PaymentDetailView />,
  },
  {
    path: '/proveedores',
    element: (
      <ProtectedRoute path="/proveedores">
        <MedicalCentersListing />
      </ProtectedRoute>
    ),
  },
  {
    path: '/proveedores/nuevo',
    element: (
      <ProtectedRoute path="/proveedores/nuevo">
        <CreateMedicalCenter />
      </ProtectedRoute>
    ),
  },
  {
    path: '/proveedores/editar/:centerId',
    element: (
      <ProtectedRoute path="/proveedores/editar/:centerId">
        <EditMedicalCenter />
      </ProtectedRoute>
    ),
  },
  {
    path: '/requerimientos',
    element: (
      <ProtectedRoute path="/requerimientos">
        <RequirementsListing />
      </ProtectedRoute>
    ),
  },
  {
    path: '/centro-medico/facturas',
    element: (
      <ProtectedRoute path="/centro-medico/facturas">
        <InvoiceList />
      </ProtectedRoute>
    ),
  },
  {
    path: '/centro-medico/facturas/nueva',
    element: (
      <ProtectedRoute path="/centro-medico/facturas/nueva">
        <CreateInvoice />
      </ProtectedRoute>
    ),
  },
  {
    path: '/centro-medico/servicios',
    element: (
      <ProtectedRoute path="/centro-medico/servicios">
        <CreateMedicalCenter />
      </ProtectedRoute>
    ),
  },
  {
    path: '/reportes/casos-medicos',
    element: (
      <ProtectedRoute path="/casos">
        <MedicalCaseReportList />
      </ProtectedRoute>
    ),
  },
  {
    path: '/chat',
    element: (
      <ProtectedRoute path="/chat">
        <Chat />
      </ProtectedRoute>
    ),
  },
  { path: '/mantenimiento', element: <Maintenance /> },
  { path: '/terminos-y-condiciones', element: <TermsConditions /> },
  { path: '/politicas-de-privacidad', element: <PrivacyPolicies /> },
  {
    path: '/api/generar-token',
    element: (
      <ProtectedRoute path="/api/generar-token">
        <GenerateToken />
      </ProtectedRoute>
    ),
  },
  {
    path: '/fondo-incurrido/abonos',
    element: (
      <ProtectedRoute path="/fondo-incurrido/abonos">
        <FundListing />
      </ProtectedRoute>
    ),
  },
  {
    path: '/fondo-incurrido/crear-pago',
    element: (
      <ProtectedRoute path="/fondo-incurrido/crear-pago">
        <CreateFund />
      </ProtectedRoute>
    ),
  },
  {
    path: '/fondo-incurrido/abonos/proveedores/:centerId',
    element: (
      <ProtectedRoute path="/fondo-incurrido/abonos/proveedores/:centerId">
        <PaymentDetailsListing />
      </ProtectedRoute>
    ),
  },
  {
    path: '/estadisticas',
    element: (
      <ProtectedRoute path="/estadisticas">
        <AnalyticsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/estudios/listado',
    element: (
      <ProtectedRoute path="/estudios/listado">
        <ImagingStudyList />
      </ProtectedRoute>
    ),
  },
  {
    path: '/estudios/informes-listos',
    element: (
      <ProtectedRoute path="/estudios/informes-listos">
        <ImagingStudyList onlyReadyReports={true} />
      </ProtectedRoute>
    ),
  },
  {
    path: '/estudios/informes-metricas',
    element: (
      <ProtectedRoute path="/estudios/informes-metricas">
        <ImagingMetrics />
      </ProtectedRoute>
    ),
  },
  {
    // Solo superusuarios: la ruta no está en ROUTE_ALLOWED_ROLES ni tiene permiso asignable
    path: '/estudios/metricas-imagenmed',
    element: (
      <ProtectedRoute path="/estudios/metricas-imagenmed">
        <ImagenMedAnalytics />
      </ProtectedRoute>
    ),
  },
  {
    path: '/estudios/equipos-centro-medico',
    element: (
      <ProtectedRoute path="/estudios/equipos-centro-medico">
        <MedicalCenterProfile />
      </ProtectedRoute>
    ),
  },
  {
    path: '/estudios/pacientes',
    element: (
      <ProtectedRoute path="/estudios/pacientes">
        <PatientsListing />
      </ProtectedRoute>
    ),
  },
  {
    path: '/doctores/listado',
    element: (
      <ProtectedRoute path="/doctores/listado">
        <DoctorR />
      </ProtectedRoute>
    ),
  },
  {
    path: '/doctores/listado/editar/:id',
    element: (
      <ProtectedRoute path="/doctores/listado/editar/:id">
        <DoctorEditPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/doctores/estadisticas',
    element: (
      <ProtectedRoute path="/doctores/estadisticas">
        <DoctorStatistics />
      </ProtectedRoute>
    ),
  },
  {
    path: '/doctorperfil',
    element: (
      <ProtectedRoute path="/doctorperfil">
        <DoctorProfilePage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/estudios/nuevo',
    element: (
      <ProtectedRoute path="/estudios/nuevo">
        <CreateImagingStudy />
      </ProtectedRoute>
    ),
  },

  // Subruta: crear desde imágenes (DICOM)
  {
    path: '/estudios/create-imaging-study',
    element: (
      <ProtectedRoute path="/estudios/create-imaging-study">
        <CreateImagingStudy />
      </ProtectedRoute>
    ),
  },
  // Subruta: crear desde audio
  {
    path: '/estudios/create-audio-study',
    element: (
      <ProtectedRoute path="/estudios/create-audio-study">
        <Audio />
      </ProtectedRoute>
    ),
  },
  {
    path: '/estudios/ver/:id',
    element: (
      <ProtectedRoute path="/estudios/ver/:id">
        <StudyViewer />
      </ProtectedRoute>
    ),
  },
  // Public route for Imagenologia quick search
  {
    path: '/inicio',
    element: <DNISearch />,
  },
  {
    path: '/estudios-doctor',
    element: <DoctorStudies />,
  },
  {
    path: '/estudios-doctor/detail/:id',
    element: <DoctorStudiesDetailV2 />,
  },
  { path: '/estudios-medicos/:id', element: <StudyViewer /> },
  // Ruta de desarrollo para probar el visor Cornerstone directamente (usa studyUID hard-codeado)
  { path: '/dev/cornerstone', element: <CornerstoneStudyURLLazy /> },
  { path: '/test-sse', element: <RealtimeTestLazy /> },
  {
    path: '/estudios/detail/:id',
    element: (
      <ProtectedRoute path="/estudios/detail/:id">
        <DetailImagingStudyV2 />
      </ProtectedRoute>
    ),
  },
  {
    path: '/estudios/audio/:id',
    element: (
      <ProtectedRoute path="/estudios/audio/:id">
        <ImagingStudyAudio />
      </ProtectedRoute>
    ),
  },
  {
    path: '/estudios/audioRecorder/:studyId',
    element: (
      <ProtectedRoute path="/estudios/audioRecorder/:studyId">
        <GenerateReport />
      </ProtectedRoute>
    ),
  },
  {
    path: '/estudios/informe/:studyId',
    element: (
      <ProtectedRoute path="/estudios/informe/:studyId">
        <Report />
      </ProtectedRoute>
    ),
  },
  {
    path: '/colectivos',
    element: (
      <ProtectedRoute path="/colectivos">
        <Colectivos />
      </ProtectedRoute>
    ),
  },
  {
    path: '/actualizaciones',
    element: (
      <ProtectedRoute path="/actualizaciones">
        <Actualizaciones />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/sync-csv',
    element: (
      <ProtectedRoute path="/admin/sync-csv">
        <SyncCsv />
      </ProtectedRoute>
    ),
  },
  {
    path: '/dria',
    element: (
      <ProtectedRoute path="/dria">
        <Dria />
      </ProtectedRoute>
    ),
  },
  // Portal del Asegurado — rutas standalone (sin ProtectedRoute del staff).
  // Con INSURED_REGISTRATION_ENABLED encendido, /asegurado/login muestra el
  // flujo de registro / código OTP e InsuredProtectedRoute exige la sesión del
  // asegurado en el resto de rutas. Apagado, funcionan como antes: acceso
  // público por cédula (patrón ImagenMed, DNISearch → /estudios-doctor).
  { path: '/asegurado/login', element: <InsuredLoginSwitch /> },
  {
    path: '/asegurado/tipo',
    element: (
      <InsuredProtectedRoute>
        <InsuredTypeSelect />
      </InsuredProtectedRoute>
    ),
  },
  {
    path: '/asegurado/nuevo',
    element: (
      <InsuredProtectedRoute>
        <InsuredNewRequest />
      </InsuredProtectedRoute>
    ),
  },
  {
    path: '/asegurado/detalle/:id',
    element: (
      <InsuredProtectedRoute>
        <InsuredRequestDetail />
      </InsuredProtectedRoute>
    ),
  },
  {
    path: '/asegurado/reembolso/detalle/:id',
    element: (
      <InsuredProtectedRoute>
        <InsuredRequestReimbursements />
      </InsuredProtectedRoute>
    ),
  },
  {
    path: '/asegurado/perfil',
    element: (
      <InsuredProtectedRoute>
        <InsuredProfile />
      </InsuredProtectedRoute>
    ),
  },
  {
    path: '/asegurado/',
    element: (
      <InsuredProtectedRoute>
        <InsuredDashboard />
      </InsuredProtectedRoute>
    ),
  },
]);

export default router;
